/**
 * TasksPage.tsx
 *
 * Full task list matching old PHP task_log.php.
 * Columns: Photo | Task Name | Patient | Plan Type | Member | Facility | Status |
 *          Priority | Occurs | Added | Due | Done
 * Filters: Past Due | Due Today | Upcoming | Complete | Inactive | All Entries
 * Filter dropdowns: Assigned To | Priority | Task Group
 *
 * Every tab renders this one table with this one column set, every data column
 * sorted server-side. Past Due and Upcoming used to swap in a grouped-by-patient
 * table with its own unsortable columns, which is why the tabs disagreed about
 * what a task list looks like.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, CheckCircle2, HelpCircle, Bot, ExternalLink, History, User } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DetailProfile } from '@/components/layout/DetailProfile';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { useAuth } from '@/hooks/useAuth';
import { MY_TASK_COUNTS_KEY } from '@/hooks/useMyTaskCounts';
import { usePatientDetail } from '@/hooks/usePatientDetail';
import { useToast } from '@/hooks/useToast';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import {
  assembleSavePayload,
  makeEmptyForm,
  pacificTodayIso,
  SEND_TO_ID_IS_RECIPIENT_TYPE,
  type TaskFormState,
} from './tasks/taskPayload';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Textarea';
import { PatientLink, usePatientNavigation } from '@/components/ui/PatientLink';
import { Avatar } from '@/components/ui/Avatar';
import { parseDateValue } from '@/lib/utils';
import { TaskDoneButton } from '@/components/tasks/TaskDoneButton';
import { useTaskReschedule } from '@/hooks/useTaskReschedule';
import { usePageContext } from '@/hooks/usePageContext';
import type { FilterOption } from '@/types/filters.types';
import * as tasksSvc from '@/services/tasks.service';
import type { Task, TaskGroup, PlanTaskRawPayload, PlanTaskTriggers, TaskHistoryEntry } from '@/services/tasks.service';
import { apiClient } from '@/services/apiClient';
import { listUsers, listFacilities, listStages, listMyRoles, listAccountRoles } from '@/services/admin.service';
import type { AdminUser, Facility, Stage, AccountRoleEntry } from '@/services/admin.service';
import { getPlansByPatient } from '@/services/plans.service';
import type { PlanListItem } from '@/services/plans.service';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import type { PatientSummary } from '@/types/patient.types';
import { buildAllTasksHref, buildAssignedToOptions, canCompleteTask, parseTaskFilterTab, priorityLabel, priorityVariant, statusVariant, TASK_FILTER_CONFIGS, TASK_FILTER_TABS, TASK_MEMBER_STATUS_OPTIONS, isTaskOpen, planNumberOrNull, taskStatusLabel as resolveTaskStatusLabel } from '@/pages/tasks/tasksShared';
import { buildApiParams, extractSearch } from '@/lib/filterRegistry';
import { buildUserChoices } from '@/lib/userChoices';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';
import { useAISidebarStore } from '@/store/aiSidebarStore';

// ─── Tooltip helper for column headers ──────────────────────────────────────

function ColHeader({ label, tip }: { label: string; tip: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help">
          {label}
          <HelpCircle className="h-3 w-3 text-slate-400" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{tip}</TooltipContent>
    </Tooltip>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(d: string | null | undefined): string {
  // Parse as a plain calendar date (local midnight) via parseDateValue so a
  // date-only "YYYY-MM-DD" isn't read as UTC midnight and shifted a day back.
  const date = parseDateValue(d);
  if (!date) return '—';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

/** Full date + time for task history headings, e.g. "Jul 1, 2026, 9:02 AM". */
function formatHistoryDateTime(d: string | null | undefined): string {
  if (!d) return '—';
  const date = new Date(String(d).replace(' ', 'T'));
  if (isNaN(date.getTime())) return String(d);
  return date.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ─── Create/Edit Modal ───────────────────────────────────────────────────────
// All enums/options below mirror the legacy plan-task-details2.php verbatim.
// Citations refer to lines in that file unless otherwise noted.


const PRIORITY_OPTIONS = [
  { value: '1', label: 'Low' },
  { value: '2', label: 'Medium' },
  { value: '3', label: 'High' },
];

// Legacy 781-792 + 921-932 — same enum used for timeType and recurringType
const TIME_UNIT_OPTIONS = [
  { value: '1', label: 'days' },
  { value: '2', label: 'weeks' },
  { value: '3', label: 'months' },
  { value: '4', label: 'years' },
];

// Legacy 795-803
const BEFORE_AFTER_OPTIONS = [
  { value: '', label: 'after' },
  { value: '-', label: 'before' },
];

// Legacy 813-839
const DATE_TYPE_OPTIONS = [
  { value: '1', label: 'assignment date' },
  { value: '9', label: 'next appointment' },
  { value: '8', label: 'next birthday' },
  { value: '2', label: 'other date' },
];

// Legacy 889-895
const OCCURRENCE_OPTIONS = [
  { value: '0', label: 'Once' },
  { value: '1', label: 'Recurring' },
];

// Legacy 737-742 — note inverted enum: 0 = "Yes (allow)", 1 = "No (required)"
const ALLOW_COMPLETION_OPTIONS = [
  { value: '0', label: 'Yes' },
  { value: '1', label: 'No' },
];

// Legacy 991-1019
const AUTO_TASK_TYPE_OPTIONS = [
  { value: '0', label: 'Human Task' },
  { value: '1', label: 'E-Mail' },
  { value: '2', label: 'SMS' },
  { value: '8', label: 'Change Plan Stage' },
  { value: '6', label: 'Add Patient Tag' },
  { value: '7', label: 'E-mail Survey Link' },
  { value: '9', label: 'SMS Survey Link' },
  { value: '3', label: 'Opt-out - E-Mail' },
  { value: '4', label: 'Opt-out - SMS' },
  { value: '5', label: 'Opt-out - E-Mail & SMS' },
];

// Legacy 1290-1301 — Send-To enum for Email autoTask
const EMAIL_SEND_TO_OPTIONS = [
  { value: '0', label: 'Not Specified' },
  { value: '1', label: 'Patient' },
  { value: '2', label: 'Coordinator' },
  { value: '3', label: 'Doctor' },
  { value: '4', label: 'Nurse' },
];

// Legacy 1257 — Send-To enum for SMS autoTask (Patient only)
const SMS_SEND_TO_OPTIONS = [
  { value: '0', label: 'Not Specified' },
  { value: '1', label: 'Patient' },
];

// Legacy 1431-1440 — appt-status enum (read-only display in this modal)
const APPT_STATUS_LABELS: Record<number, string> = {
  0: 'None', 1: 'Requested', 2: 'Scheduled', 3: 'Confirmed', 4: 'Checked In',
  5: 'Checked Back', 6: 'Checked Out', 7: 'Cancelled', 8: 'No Show',
  9: 'Wait List', 10: 'Contact Attempted',
};

// 0-364 select options for timeAmount and recurringAmount
const TIME_AMOUNT_OPTIONS = Array.from({ length: 365 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));
// 1-364 for recurringAmount (legacy line 912)
const RECURRING_AMOUNT_OPTIONS = Array.from({ length: 364 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));
// 2-100 for recurringNumber (legacy line 902)
const RECURRING_NUMBER_OPTIONS = Array.from({ length: 99 }, (_, i) => ({
  value: String(i + 2),
  label: String(i + 2),
}));

// ─── Helpers for legacy assignedToID parsing ────────────────────────────────

/**
 * Detect the legacy assignedToID family (legacy lines 1031-1078).
 * Used to show/hide the conditional Specific User selector and to format the
 * dropdown's selected value when the row stores either form of group_X.
 */
function isUserAssignment(assignedToID: string | null | undefined): boolean {
  return assignedToID === 'user';
}

// ─── Lookup data fetchers ────────────────────────────────────────────────────
// Inline fetchers for modal-only reference data. Kept here (not in
// tasks.service.ts) because they're only used by this modal and re-using the
// generic apiClient envelope makes shape adaptation explicit.

interface CannedTaskRow { taskID: number; name: string; }
interface TaskCategoryRow { taskCategoryID: number; masterTaskCategoryID: number; taskID: number; title: string; }
interface TagRow { tagsCustomID: number; title: string; }
interface SurveyRow { id: number; title: string; }
interface AdminRoleRow { accountRoleID: number; roleID?: number; roleTitle: string; roleType?: string; endDate?: string; }
/**
 * Row from the legacy `roles` table — the actual custom-roles list
 * (one row per role; account has many). Distinct from `aa_accounts_roles`,
 * which only stores the four "From Plan" label overrides for the hardcoded
 * Doctor/Coordinator/Nurse/Receptionist tokens.
 *
 * Fetched via GET /filters/options?types[]=group, which returns
 * `{ value: roleID, label: title }` from `roles` where endDate='0000-00-00 00:00:00'.
 */
interface AccountRoleRow { roleId: number; roleTitle: string; }
interface CustomStageRow { accountCustomStageID: number; stageID: number | null; stageName: string; }
interface DocumentRowGrouped { categoryName: string; documents: Array<{ documentID: number; title: string; fileName: string; }>; }
interface DocumentPacketRow { documentPacketId: number; name: string; description?: string; }
interface PickerCategory { id: string; name: string; templates: Array<{ id: string; name: string; }>; }
interface PickerHeaderItem { id: string; name: string; }

async function fetchCannedTasks(): Promise<CannedTaskRow[]> {
  const r = await apiClient.get<{ data: CannedTaskRow[] }>('/tasks', { params: { active: true } });
  return r.data.data ?? [];
}
async function fetchTaskCategories(): Promise<TaskCategoryRow[]> {
  const r = await apiClient.get<{ data: TaskCategoryRow[] }>('/task-categories');
  return r.data.data ?? [];
}
async function fetchTagsCustom(): Promise<TagRow[]> {
  const r = await apiClient.get<{ data: TagRow[] }>('/tags-custom', { params: { active: true } });
  return r.data.data ?? [];
}
async function fetchSurveys(): Promise<SurveyRow[]> {
  const r = await apiClient.get<{ data: SurveyRow[] }>('/surveys');
  return r.data.data ?? [];
}
async function fetchAdminRoles(): Promise<AdminRoleRow[]> {
  const r = await apiClient.get<{ data: AdminRoleRow[] }>('/admin/roles');
  return (r.data.data ?? []).filter((row) => !row.endDate || row.endDate === '0000-00-00 00:00:00');
}
/**
 * Fetch the active custom roles from the legacy `roles` table via
 * FilterController::options('group'). Used for the Assigned To dropdown's
 * `role_<X>` (Individual Tasks) / `group_<X>` (Single Task) entries.
 *
 * Critical: the legacy `roles` table is NOT the same as `aa_accounts_roles`.
 * The latter only holds the four hardcoded role-type label overrides
 * (Doctor/Coordinator/Nurse/Receptionist). Conflating the two surfaced
 * the wrong rows in the dropdown — see the audit synthesis category J/H.
 */
async function fetchAccountRoles(): Promise<AccountRoleRow[]> {
  const r = await apiClient.get<{ data: { group?: Array<{ value: string; label: string }> } }>(
    '/filters/options',
    { params: { 'types[]': 'group' } },
  );
  return (r.data.data?.group ?? []).map((o) => ({
    roleId: Number(o.value),
    roleTitle: o.label,
  }));
}
async function fetchCustomStages(): Promise<CustomStageRow[]> {
  const r = await apiClient.get<{ data: CustomStageRow[] }>('/account-custom-stages');
  return r.data.data ?? [];
}
async function fetchEmailContent(): Promise<PickerCategory[]> {
  const r = await apiClient.get<{ categories: PickerCategory[] }>('/email-templates/content');
  return r.data.categories ?? [];
}
async function fetchEmailHeaders(): Promise<PickerHeaderItem[]> {
  const r = await apiClient.get<{ headers: PickerHeaderItem[] }>('/email-templates/headers');
  return r.data.headers ?? [];
}
async function fetchSmsTemplates(): Promise<PickerCategory[]> {
  const r = await apiClient.get<{ categories: PickerCategory[] }>('/sms-templates/picker');
  return r.data.categories ?? [];
}
async function fetchDocumentsGrouped(): Promise<DocumentRowGrouped[]> {
  const r = await apiClient.get<{ data: DocumentRowGrouped[] }>('/documents');
  return r.data.data ?? [];
}
async function fetchDocumentPackets(): Promise<DocumentPacketRow[]> {
  const r = await apiClient.get<{ data: DocumentPacketRow[] }>('/document-packets/templates');
  return r.data.data ?? [];
}

// Flatten the picker shape to a flat option list for <Select>
function flattenPickerCategories(cats: PickerCategory[]): Array<{ value: string; label: string }> {
  return cats.flatMap((c) => c.templates.map((t) => ({ value: String(t.id), label: `${c.name} — ${t.name}` })));
}

/**
 * Initialize TaskFormState from a Task row, mapping every persisted plan_tasks
 * column back into the modal's form fields. Mirrors legacy plan-task-details2
 * lines 236-298.
 */
function initialFormFromItem(item: Task): TaskFormState {
  const dateStartedISO = item.dateStarted && item.dateStarted !== '0000-00-00'
    ? new Date(item.dateStarted).toISOString().slice(0, 10)
    : pacificTodayIso();
  const customDateRaw = item.customDate && item.customDate !== '0000-00-00' && item.customDate !== '0000-00-00 00:00:00'
    ? new Date(item.customDate).toISOString().slice(0, 10)
    : pacificTodayIso(); // legacy line 339 default
  const ta = item.timeAmount ?? 0;
  const beforeOrAfter = ta < 0 ? '-' : '';
  const timeAmountAbs = String(Math.abs(ta));

  // Resolve the conditional content fields back from contentID / headerFooterID
  // depending on autoTaskID (legacy quirks confirmed in 0.1).
  const auto = item.autoTaskID != null ? String(item.autoTaskID) : '0';
  const contentID = item.contentID != null ? String(item.contentID) : '0';
  const headerFooterID = item.headerFooterID != null ? String(item.headerFooterID) : '0';
  const sendToID = item.sendToID != null ? String(item.sendToID) : '0';

  return {
    taskID: item.taskID ? String(item.taskID) : (item.name ? 'custom' : ''),
    customTaskName: item.taskID ? '' : (item.name ?? ''),
    patientID: item.taskPatientID ? String(item.taskPatientID) : '',
    planID: item.planID ? String(item.planID) : '',
    priority: String(item.priority ?? 2),
    required: String(item.required ?? 0),
    active: String(item.active ?? 1),

    timeAmount: timeAmountAbs,
    timeType: item.timeType != null ? String(item.timeType) : '1',
    beforeOrAfter,
    dateType: item.dateType != null ? String(item.dateType) : '2',
    customDate: customDateRaw,
    dateStarted: dateStartedISO,

    recurring: String(item.recurring ?? 0),
    recurringNumber: item.recurringNumber != null ? String(item.recurringNumber) : '2',
    recurringAmount: item.recurringAmount != null ? String(item.recurringAmount) : '1',
    recurringType: item.recurringType != null ? String(item.recurringType) : '1',

    notes: item.notes ?? '',

    // assignedToID is preserved verbatim (coord/nurse/doc/rec/role_X/group_X/user/ftp/group)
    // Legacy stores group-assigned tasks as assignedToID='group' + sendToID=roleId;
    // normalize to 'group_<roleId>' so it matches the dropdown option value.
    assignedToID: item.assignedToID === 'group' && item.sendToID
      ? `group_${item.sendToID}`
      : (item.assignedToID && item.assignedToID !== '' ? item.assignedToID : 'coord'),
    assignedUserID: isUserAssignment(item.assignedToID) && item.sendToID ? String(item.sendToID) : '',
    // Edit keeps the single assignee above; the multi-picker is create-only.
    assignedUserIDs: [],

    autoTaskID: auto,

    sendEmailToID: auto === '1' ? sendToID : '0',
    emailHeaderFooterID: auto === '1' ? headerFooterID : '0',
    emailContentID: auto === '1' ? contentID : '0',

    sendSMSToID: auto === '2' ? sendToID : '0',
    smsContentID: auto === '2' ? contentID : '0',

    tagCustomID: auto === '6' ? contentID : '0',

    surveyID: (auto === '7' || auto === '9') ? headerFooterID : '0',
    surveyEmailContentID: auto === '7' ? contentID : '0',
    surveySmsContentID: auto === '9' ? contentID : '0',

    attachments: item.attachments ?? '',
    noteID: item.noteID != null ? String(item.noteID) : '0',
  };
}


export function TaskModal({ item, onClose, onSaved, initialPatient, initialPlan, initialNoteId }: {
  item?: Task | null;
  onClose: () => void;
  onSaved: () => void;
  users?: AdminUser[]; // legacy prop, no longer used directly (Specific User picker reads from /admin/roles + adminUsers)
  initialPatient?: PatientSummary;
  /** Create mode only: pre-assign the new task to this plan and show the
   *  Patient & Plan pair read-only. Used by the plan-scoped Tasks section,
   *  where the parent plan is context, not a choice the user makes. */
  initialPlan?: { planId: number; planLabel: string; patientId: number; patientName: string };
  /** Create mode only: link the new task to this note via plan_tasks.noteID,
   *  so the task records which note it was raised from. Set by the "Add Task"
   *  button on a note. The column is long-standing and already written on every
   *  insert (as 0); legacy filled it from the per-note task panel, which was
   *  switched off - see note-load-task-display.php. */
  initialNoteId?: number;
}) {
  const isEdit = item != null;
  const queryClient = useQueryClient();
  // Every save path here changes the user's task buckets. Refresh the shared
  // task counts (the sidebar's Tasks count, the Dashboard's Pending Tasks
  // badge) here, so callers that only close the dialog — creating a task from
  // a note, for one — still update them.
  function finishSave() {
    void queryClient.invalidateQueries({ queryKey: MY_TASK_COUNTS_KEY });
    onSaved();
    onClose();
  }
  // Only meaningful on create — on edit the plan comes from the task itself.
  const lockedPlan = isEdit ? null : initialPlan ?? null;
  const { isAdmin: modalIsAdmin, user: modalAuthUser } = useAuth();
  const overrideFacilityId = useGlobalFacilityStore((s) => s.selectedFacilityIds[0] ?? null);

  // ── Form state ──────────────────────────────────────────────────────────
  // On edit, fetch the full row via getTask first so we have every column
  // (the row from the list is missing autoTaskID/contentID/etc. per audit B4).
  const [form, setForm] = useState<TaskFormState>(() =>
    item ? initialFormFromItem(item) : {
      ...makeEmptyForm(),
      ...(initialPatient ? { patientID: String(initialPatient.patientId) } : {}),
      ...(initialPlan
        ? { patientID: String(initialPlan.patientId), planID: String(initialPlan.planId) }
        : {}),
      ...(initialNoteId ? { noteID: String(initialNoteId) } : {}),
    },
  );
  const [loadingItem, setLoadingItem] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [rescheduling, setRescheduling] = useState<null | 'week' | 'month'>(null);
  const [error, setError] = useState<string | null>(null);
  // Set by a Save refused for want of a Custom Date, so the field itself says so.
  const [customDateMissing, setCustomDateMissing] = useState(false);
  const customDateRef = useRef<HTMLInputElement>(null);
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(initialPatient ?? null);
  const toast = useToast();

  // ── Patient this task belongs to (edit mode) ────────────────────────────
  // Seeded from the list row (which carries a pre-joined patientName) so the
  // header shows it immediately, then refreshed from GET /plan-tasks/{id},
  // which resolves it even when the caller's row had no patient fields.
  const [taskPatient, setTaskPatient] = useState<{ id: number | null; name: string } | null>(() => {
    if (!item) return null;
    const name = item.patientName?.trim()
      || `${item.patientFirstName ?? ''} ${item.patientLastName ?? ''}`.trim();
    const id = item.patientID ?? item.taskPatientID ?? null;
    return name ? { id, name } : null;
  });

  // ── Reference data ──────────────────────────────────────────────────────
  const [cannedTasks, setCannedTasks] = useState<CannedTaskRow[]>([]);
  const [taskCategories, setTaskCategories] = useState<TaskCategoryRow[]>([]);
  // Transient browsing aid for the Type dropdown — not a persisted form field.
  const [typeCategoryFilter, setTypeCategoryFilter] = useState('');
  // adminRoles = aa_accounts_roles (4 rows max). Used ONLY for resolving the
  // custom title overrides on the four "From Plan" dropdown entries
  // (roleType => roleTitle), per legacy plan-task-details2.php:39-67.
  const [adminRoles, setAdminRoles] = useState<AdminRoleRow[]>([]);
  // accountRoles = legacy `roles` table (one row per active custom role).
  // Used for the role_<X> (Individual Tasks) / group_<X> (Single Task) dropdown
  // entries, per legacy plan-task-details2.php:1059-1068.
  const [accountRoles, setAccountRoles] = useState<AccountRoleRow[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [tags, setTags] = useState<TagRow[]>([]);
  const [surveys, setSurveys] = useState<SurveyRow[]>([]);
  const [emailContent, setEmailContent] = useState<PickerCategory[]>([]);
  const [emailHeaders, setEmailHeaders] = useState<PickerHeaderItem[]>([]);
  const [smsCategories, setSmsCategories] = useState<PickerCategory[]>([]);
  const [globalStages, setGlobalStages] = useState<Stage[]>([]);
  const [customStages, setCustomStages] = useState<CustomStageRow[]>([]);
  const [triggers, setTriggers] = useState<PlanTaskTriggers | null>(null);
  const [documentsGrouped, setDocumentsGrouped] = useState<DocumentRowGrouped[]>([]);
  const [docPackets, setDocPackets] = useState<DocumentPacketRow[]>([]);

  // PlanListItem: getPlansByPatient returns it, and setPatientPlans is
  // handed straight to that promise.
  const [patientPlans, setPatientPlans] = useState<PlanListItem[]>([]);

  // ── Effect: fetch full task on edit, replace form ──────────────────────
  useEffect(() => {
    if (!isEdit) return;
    setLoadingItem(true);
    void tasksSvc.getTask(item.planTaskID)
      .then((full) => {
        setForm(initialFormFromItem(full));
        const name = full.patientName?.trim();
        if (name) setTaskPatient({ id: full.patientID ?? full.taskPatientID ?? null, name });
      })
      .catch(() => { /* keep optimistic form built from list row */ })
      .finally(() => setLoadingItem(false));
  }, [isEdit, item?.planTaskID]);

  // ── Effect: fetch task change history on edit (plan_tasks_history feed) ──
  const [history, setHistory] = useState<TaskHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(isEdit);
  const [historyError, setHistoryError] = useState(false);
  useEffect(() => {
    if (!isEdit) return;
    setHistoryLoading(true);
    setHistoryError(false);
    void tasksSvc.getTaskHistory(item.planTaskID)
      .then(setHistory)
      .catch(() => setHistoryError(true))
      .finally(() => setHistoryLoading(false));
  }, [isEdit, item?.planTaskID]);

  // ── Effect: load all reference data on open ────────────────────────────
  useEffect(() => {
    void fetchCannedTasks().then(setCannedTasks).catch(() => setCannedTasks([]));
    void fetchTaskCategories().then(setTaskCategories).catch(() => setTaskCategories([]));
    void fetchAdminRoles().then(setAdminRoles).catch(() => setAdminRoles([]));
    void fetchAccountRoles().then(setAccountRoles).catch(() => setAccountRoles([]));
    void listUsers().then(setAdminUsers).catch(() => setAdminUsers([]));
    void fetchTagsCustom().then(setTags).catch(() => setTags([]));
    void fetchSurveys().then(setSurveys).catch(() => setSurveys([]));
    void fetchEmailContent().then(setEmailContent).catch(() => setEmailContent([]));
    void fetchEmailHeaders().then(setEmailHeaders).catch(() => setEmailHeaders([]));
    void fetchSmsTemplates().then(setSmsCategories).catch(() => setSmsCategories([]));
    void listStages().then(setGlobalStages).catch(() => setGlobalStages([]));
    void fetchCustomStages().then(setCustomStages).catch(() => setCustomStages([]));
  }, []);

  // ── Effect: load triggers + documents only on edit (and docs only when needed) ──
  useEffect(() => {
    if (!isEdit) return;
    void tasksSvc.getPlanTaskTriggers(item.planTaskID)
      .then(setTriggers)
      .catch(() => setTriggers({ addAtStage: [], removeAtStage: [], addAtApptStage: [], removeAtApptStage: [] }));
  }, [isEdit, item?.planTaskID]);

  useEffect(() => {
    if (form.autoTaskID !== '1') return; // Phase 0 decision #7: documents only for Email
    if (documentsGrouped.length > 0 || docPackets.length > 0) return; // already loaded
    void fetchDocumentsGrouped().then(setDocumentsGrouped).catch(() => setDocumentsGrouped([]));
    void fetchDocumentPackets().then(setDocPackets).catch(() => setDocPackets([]));
  }, [form.autoTaskID, documentsGrouped.length, docPackets.length]);

  // ── Effect: load plans when patientID changes (NEW only) ───────────────
  // Skipped when the plan is locked by the caller — the picker it feeds is not
  // rendered, so the request would be dead weight.
  useEffect(() => {
    if (lockedPlan) return;
    const pid = form.patientID ? Number(form.patientID) : null;
    if (pid && pid > 0) {
      void getPlansByPatient(pid).then(setPatientPlans).catch(() => setPatientPlans([]));
    } else {
      setPatientPlans([]);
    }
  }, [form.patientID, lockedPlan]);

  // ── Derived: role title overrides for "From Plan" labels ───────────────
  const roleTitle = useMemo(() => {
    const find = (type: string) =>
      adminRoles.find(r => r.roleType === type)?.roleTitle;
    return {
      coord: find('Coordinator') ?? 'Coordinator',
      nurse: find('Nurse') ?? 'Nurse',
      doc:   find('Doctor') ?? 'Doctor',
      rec:   'Receptionist', // legacy line 1043 hardcodes this label
    };
  }, [adminRoles]);

  // Same overrides applied to the Send-To enum, whose 2/3/4 name the same roles.
  const emailSendToOptions = useMemo(
    () => EMAIL_SEND_TO_OPTIONS.map((o) => {
      if (o.value === '2') return { ...o, label: roleTitle.coord };
      if (o.value === '3') return { ...o, label: roleTitle.doc };
      if (o.value === '4') return { ...o, label: roleTitle.nurse };
      return o;
    }),
    [roleTitle],
  );

  // ── Derived: stage title map merging global + account custom ───────────
  // Mirrors legacy lines 1374-1381 (custom stages) + 73-79 (global stages)
  const stageTitleMap = useMemo(() => {
    const map: Record<number, string> = {};
    for (const s of globalStages) {
      map[s.stageId] = s.stageName;
    }
    for (const cs of customStages) {
      if (cs.stageID) map[cs.stageID] = cs.stageName;
      map[cs.accountCustomStageID] = cs.stageName;
    }
    return map;
  }, [globalStages, customStages]);

  // ── Derived: dropdown option lists ─────────────────────────────────────
  // taskID → category assignment, from GET /task-categories (optional per task).
  const taskCategoryByTaskID = useMemo(() => {
    const map: Record<number, { masterTaskCategoryID: number; title: string }> = {};
    for (const c of taskCategories) {
      map[c.taskID] = { masterTaskCategoryID: c.masterTaskCategoryID, title: c.title };
    }
    return map;
  }, [taskCategories]);

  // Distinct categories actually in use, for the "Category" filter dropdown.
  const categoryFilterOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const c of taskCategories) {
      if (!seen.has(c.masterTaskCategoryID)) seen.set(c.masterTaskCategoryID, c.title);
    }
    const sorted = [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    return [
      { value: '', label: 'All Categories' },
      ...sorted.map(([id, title]) => ({ value: String(id), label: title })),
    ];
  }, [taskCategories]);

  const cannedTaskOptions = useMemo(() => {
    const filtered = typeCategoryFilter
      ? cannedTasks.filter(t => String(taskCategoryByTaskID[t.taskID]?.masterTaskCategoryID ?? '') === typeCategoryFilter)
      : cannedTasks;
    return [
      { value: '', label: 'Select one...' },
      { value: 'custom', label: 'New Custom Task' },
      ...filtered.map(t => ({ value: String(t.taskID), label: t.name })),
    ];
  }, [cannedTasks, taskCategoryByTaskID, typeCategoryFilter]);

  // Single combined Assigned To dropdown matching legacy lines 1031-1078.
  // FTP gating per legacy line 1074: userLevel > 2 OR ftp already selected.
  const assignedToOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [];
    const planIsLinked = !!form.planID && form.planID !== '0';
    if (planIsLinked) {
      opts.push({ value: 'coord', label: `${roleTitle.coord} From Plan` });
      opts.push({ value: 'nurse', label: `${roleTitle.nurse} From Plan` });
      opts.push({ value: 'doc',   label: `${roleTitle.doc} From Plan` });
      opts.push({ value: 'rec',   label: `${roleTitle.rec} From Plan` });
    }
    // Iterate the legacy `roles` table (accountRoles), NOT aa_accounts_roles.
    // aa_accounts_roles only holds the 4 hardcoded role-type label overrides
    // and has no meaningful Individual/Single Task semantics.
    for (const r of accountRoles) {
      opts.push({ value: `role_${r.roleId}`,  label: `${r.roleTitle} (Individual Tasks)` });
      opts.push({ value: `group_${r.roleId}`, label: `${r.roleTitle} (Single Task)` });
    }
    opts.push({ value: 'user', label: 'User' });
    if (modalIsAdmin || form.assignedToID === 'ftp') {
      opts.push({ value: 'ftp', label: 'Prospera' });
    }
    return opts;
  }, [accountRoles, roleTitle, form.planID, form.assignedToID, modalIsAdmin]);

  const simpleAssignedToOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [
      { value: 'myself', label: 'Myself' },
      { value: 'user', label: 'User' },
      { value: 'group', label: 'Group' },
    ];
    if (modalIsAdmin) {
      opts.push({ value: 'ftp', label: 'Prospera' });
    }
    return opts;
  }, [modalIsAdmin]);

  const groupPickerOptions = useMemo(
    () => accountRoles.flatMap(r => [
      { value: `role_${r.roleId}`, label: `${r.roleTitle} (Individual Tasks)` },
      { value: `group_${r.roleId}`, label: `${r.roleTitle} (Single Task)` },
    ]),
    [accountRoles],
  );

  const currentUserId = String(modalAuthUser?.id ?? '');

  // Picking several people creates one task each, so it only applies to a new
  // task. Editing changes the one task in front of you. Also unavailable for
  // the auto-task types whose send-target shares the column (see
  // SEND_TO_ID_IS_RECIPIENT_TYPE).
  const canPickMultipleUsers = !isEdit && !SEND_TO_ID_IS_RECIPIENT_TYPE.includes(form.autoTaskID);

  const assignedCategory = form.assignedToID === 'myself' ? 'myself'
    : form.assignedToID === 'user' ? 'user'
    : form.assignedToID === 'ftp' ? 'ftp'
    : form.assignedToID.startsWith('group_') || form.assignedToID.startsWith('role_') ? 'group'
    : 'user';

  // /admin/users carries deactivated staff too, so the picker filters them out
  // itself — except whoever this task is already on, who stays listed and
  // marked. `form.assignedUserID` is the task's stored sendToID on edit.
  const userOptions = useMemo(
    () => buildUserChoices(adminUsers, form.assignedUserID)
      .map(c => ({ value: String(c.userId), label: c.label })),
    [adminUsers, form.assignedUserID],
  );

  const planOptions = patientPlans.map(p => ({
    value: String(p.planId),
    label: p.displayId ? `#${p.displayId} — ${p.patientName}` : `Plan #${p.planId}`,
  }));

  const tagOptions = useMemo(
    () => [{ value: '0', label: 'Select one...' }, ...tags.map(t => ({ value: String(t.tagsCustomID), label: t.title }))],
    [tags],
  );
  const surveyOptions = useMemo(
    () => [{ value: '0', label: 'Not Specified' }, ...surveys.map(s => ({ value: String(s.id), label: s.title }))],
    [surveys],
  );
  const emailContentOptions = useMemo(
    () => [{ value: '0', label: 'Not Specified' }, ...flattenPickerCategories(emailContent)],
    [emailContent],
  );
  const emailHeaderOptions = useMemo(
    () => [{ value: '0', label: 'Not Specified' }, ...emailHeaders.map(h => ({ value: String(h.id), label: h.name }))],
    [emailHeaders],
  );
  const smsContentOptions = useMemo(
    () => [{ value: '0', label: 'Not Specified' }, ...flattenPickerCategories(smsCategories)],
    [smsCategories],
  );

  // ── Derived: parsed attachments set for checkbox state ─────────────────
  const attachmentSet = useMemo(() => {
    if (!form.attachments) return new Set<string>();
    return new Set(form.attachments.split(',').map(s => s.trim()).filter(Boolean));
  }, [form.attachments]);

  function set<K extends keyof TaskFormState>(k: K, v: TaskFormState[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  // L64: attachments serialize as a comma-separated CSV matching legacy
  // plan-task-processor.php:350 / plan-task-details2.php:273-280:
  //   - "P_<documentPacketId>" entries for packet selections
  //   - bare "<documentID>" entries for individual documents
  // The packet/document checkbox values constructed in the Documents section
  // emit those exact shapes, and we recombine them via Array.join(',') so the
  // backend writes the same string legacy did.
  function toggleAttachment(value: string) {
    const next = new Set(attachmentSet);
    if (next.has(value)) next.delete(value); else next.add(value);
    set('attachments', Array.from(next).join(','));
  }

  // ── Save handler ───────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // A locked task has no Save button, but the <form> still owns onSubmit, so
    // Enter in any field would post the (unchanged) form. Refuse outright.
    if (taskInactive) return;
    // Validate: must have a task name (custom or canned)
    const hasName = form.taskID && form.taskID !== '' && form.taskID !== 'custom'
      ? true
      : form.customTaskName.trim().length > 0;
    if (!hasName) {
      setError('Please select a task type or enter a custom task name.');
      return;
    }
    if (canPickMultipleUsers && form.assignedToID === 'user' && form.assignedUserIDs.length === 0) {
      setError('Choose at least one person to assign this task to.');
      return;
    }
    // "other date" is the Custom Date, so it needs one. Saved without it, the
    // server stores 0000-00-00 and works the due date out from that.
    if (form.dateType === '2' && !form.customDate) {
      setCustomDateMissing(true);
      setError('Choose a Custom Date. It is required when Date to Perform is "other date".');
      customDateRef.current?.focus();
      return;
    }
    setSaving(true); setError(null);
    try {
      const resolvedForm = form.assignedToID === 'myself'
        ? { ...form, assignedToID: 'user', assignedUserID: currentUserId, assignedUserIDs: [currentUserId] }
        : form;
      const payload = assembleSavePayload(resolvedForm);
      // assembleSavePayload only fills `name` for custom tasks; for a canned
      // taskID it leaves it '', and the backend persists that blank straight
      // into plan_tasks.name — wiping the title. Resolve the canned task's
      // denormalized name from the selected Type (falling back to the existing
      // title on edit) so saving never blanks it.
      if (!payload.name) {
        const cannedName = cannedTasks.find(t => String(t.taskID) === resolvedForm.taskID)?.name;
        payload.name = cannedName ?? item?.name ?? '';
      }
      if (isEdit) {
        await tasksSvc.updatePlanTaskRaw(item.planTaskID, payload);
      } else {
        // Create needs a few extra fields the assembler doesn't populate
        const createPayload: PlanTaskRawPayload = {
          ...payload,
          planID: form.planID ? parseInt(form.planID, 10) : 0,
          taskPatientID: form.patientID ? parseInt(form.patientID, 10) : 0,
          pending: 1, // mirrors legacy plan-task-processor.php new task stub
          active: 1,
        };
        // facilityID is set by the backend from the linked plan/patient,
        // but the global facility selector wins when present (matches legacy)
        if (overrideFacilityId) {
          (createPayload as PlanTaskRawPayload & { facilityID?: number }).facilityID = overrideFacilityId;
        }
        await tasksSvc.createPlanTaskRaw(createPayload);
      }
      finishSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task.');
    } finally {
      setSaving(false);
    }
  }

  // ── Deactivate handler ─────────────────────────────────────────────────
  // Legacy called the SAME endpoint as the Done button with a canned note
  // (assets-ftp/js/tasks.js:283 → task_set_complete.php), so a de-activated
  // task was stored as a completion and showed the green "Completed" badge.
  // This hits PATCH /tasks/{id}/deactivate instead: pending=0 with
  // dateCompleted left at the zero sentinel, i.e. status 'Inactive', and no
  // recurrence advance. The canned note is kept — it is what tells staff who
  // de-activated it, and it is the note the comm_log entry carries.
  async function handleDeactivate() {
    if (!isEdit) return;
    if (!window.confirm('De-activate task?')) return;
    const userFullName = modalAuthUser?.displayName?.trim() || 'unknown user';
    setDeactivating(true); setError(null);
    try {
      await tasksSvc.deactivateTask(item.planTaskID, {
        notes: `Manually de-activated by ${userFullName}`,
      });
      toast.success('Task de-activated');
      finishSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate task.');
    } finally {
      setDeactivating(false);
    }
  }

  // ── Complete handler ───────────────────────────────────────────────────
  // Same endpoint as the list's Done button, with the Notes box doubling as the
  // completion note (the server overwrites plan_tasks.notes when one is posted),
  // so this is the identical request CompleteModal sends. Other unsaved field
  // edits are intentionally discarded — saving first would fire
  // updatePlanTaskRaw, snapshotting a spurious 'Due' history row and possibly
  // moving lastDue out from under the completion. Matches handleDeactivate.
  async function handleComplete() {
    if (!isEdit) return;
    if (!window.confirm('Mark this task complete? Other unsaved changes on this form will not be saved.')) return;
    setCompleting(true); setError(null);
    try {
      await tasksSvc.completeTask(item.planTaskID, { notes: form.notes || undefined });
      toast.success('Task completed');
      finishSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete task.');
    } finally {
      setCompleting(false);
    }
  }

  // Quick-reschedule: push lastDue to today + 1 week / 1 month (Pacific-anchored,
  // computed server-aligned in tasksSvc.rescheduleTaskFromToday). Reuses the
  // PUT /plan-tasks/{id} path, which snapshots history as a 'Due' change. Only
  // shifts the due date — pending/dateCompleted are left untouched, so the task
  // simply re-buckets from Past Due/Due into Upcoming.
  async function handleReschedule(unit: 'week' | 'month') {
    if (!isEdit) return;
    setRescheduling(unit); setError(null);
    try {
      await tasksSvc.rescheduleTaskFromToday(item.planTaskID, unit);
      toast.success('Task rescheduled', `Now due 1 ${unit} from today.`);
      finishSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reschedule task.');
    } finally {
      setRescheduling(null);
    }
  }

  // ── UI flags ───────────────────────────────────────────────────────────
  const isRecurring = form.recurring === '1';
  const showCustomDate = form.dateType === '2';
  const showCustomNameInput = !isEdit && (form.taskID === 'custom' || (form.taskID === '' && !!form.customTaskName));
  const auto = form.autoTaskID;
  const showEmailFields = auto === '1';
  const showSMSFields = auto === '2';
  const showTagField = auto === '6';
  const showEmailSurveyFields = auto === '7';
  const showSMSSurveyFields = auto === '9';
  const showDocumentsSection = auto === '1'; // Phase 0 decision #7

  // Resolve the status the same way the badge does, rather than reading
  // `derivedStatus` raw: GET /plan-tasks/{id} (what this modal loads) returns a
  // differently-worded `status` and no `derivedStatus` at all, and a row handed
  // in from the Plans tab may carry neither, so the raw field cannot be trusted
  // to be present. resolveTaskStatusLabel falls back to the same CASE the
  // server uses.
  const taskStatusLabel = item ? resolveTaskStatusLabel(item) : '';
  // Locked whenever the task is in a terminal state — which is exactly the two
  // labels that mean "closed": Completed and Inactive.
  //
  // This used to be `item?.pending === 0`, copied from legacy
  // plan-task-details2.php:478 ($taskStatus=="Inactive" ⟺ pending=="0"). That
  // catches completions and de-activations but MISSES the other two ways a row
  // renders as Inactive under the derivedStatus CASE — pending = 2, and
  // pending = 1 with no due date at all ('0000-00-00', e.g. a birthday task on
  // a patient with no date of birth, or an appointment-anchored task with no
  // appointment). Those tasks showed the grey "Inactive" badge above a fully
  // editable form with live Save / Complete / De-Activate buttons.
  //
  // Do NOT key off `active`: FTP hard-codes active=0 on every hand-created task
  // (plan_task_processor.php:54), so it is a create-time default, not a flag.
  const taskInactive = isEdit && item != null && !isTaskOpen(item);
  // The enclosing footer group is already gated on `isEdit && !taskInactive`
  // (both terminal labels ⇒ taskInactive), so the only extra rule left is the
  // legacy "can't complete before due" flag, shared with the list Done column.
  const showComplete = isEdit && !taskInactive && item != null && canCompleteTask(item);
  const actionsBusy = saving || deactivating || completing || rescheduling !== null;

  // Read-only patient/plan display — edit mode, plus create-from-a-plan, where
  // the caller supplies the pair instead of the task row.
  const patientId = item?.taskPatientID ?? lockedPlan?.patientId ?? 0;
  const patientLabel = item?.patientName?.trim() || lockedPlan?.patientName.trim() || '';
  const planDisplayNo = planNumberOrNull(item?.planDisplayID);
  const planNo = planNumberOrNull(item?.planID);
  const planLabel = item
    ? (planDisplayNo
        ? `#${planDisplayNo}`
        : planNo
          ? `Plan #${planNo}`
          : 'No plan')
    : lockedPlan?.planLabel ?? 'No plan';

  return (
    <Modal isOpen onClose={onClose} size="lg"
      title={
        <>
          <h2 className="text-base font-semibold text-slate-900 truncate">
            {isEdit ? `Edit Task${item?.name ? ` — ${item.name}` : ''}` : 'Add Task'}
          </h2>
          {isEdit && taskPatient && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
              <User className="h-3 w-3 flex-shrink-0 text-slate-400" />
              {taskPatient.id ? (
                <PatientLink
                  patientId={taskPatient.id}
                  patientName={taskPatient.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate font-medium text-slate-600 hover:text-primary hover:underline"
                >
                  {taskPatient.name}
                </PatientLink>
              ) : (
                <span className="truncate font-medium text-slate-600">{taskPatient.name}</span>
              )}
            </p>
          )}
        </>
      }
      footer={
        /* Same shape CompleteModal below already uses, and for the same reason: the
           shared footer row is `justify-end` with no wrap, so own the layout here.
           The unprefixed classes reproduce today's computed row exactly (the outer
           footer is `flex items-center justify-end gap-3`, and `w-full` makes this
           single child fill it), so desktop resolves to what it resolves to now;
           every mobile rule is `max-md:`-prefixed. */
        <div className="flex w-full items-center justify-end gap-3 max-md:flex-col max-md:items-stretch max-md:gap-2">
          {/* Complete anchors the far left, the three secondary actions sit
              after it at sm, and Save Changes stays pinned right via mr-auto.
              flex-wrap is the safety net: six default-size buttons overflowed
              the max-w-2xl panel, so if labels ever grow the group wraps
              instead of spilling past the modal edge. No Cancel button — the
              header X already closes the modal. */}
          {/*
            SCRUM-1404 reads as "the Add Task footer overlaps the form". It cannot —
            Modal is `flex flex-col` with a `flex-shrink-0` footer and a `flex-1
            overflow-y-auto` body, so the footer is a flow sibling. What actually
            happens is this group wrapping to three or four rows on a phone and taking
            that height out of the body's flex share, which hides the card below
            "Occurrence" behind it. Stacking the group and giving the buttons full
            width is the fix, and it is the treatment CompleteModal in this same file
            already carries.
          */}
          {isEdit && !taskInactive && (
            <div className="mr-auto flex flex-wrap items-center gap-2 max-md:mr-0 max-md:grid max-md:w-full max-md:grid-cols-2">
              {showComplete && (
                <Button
                  variant="secondary"
                  className="border-green-300 text-green-700 hover:bg-green-50 max-md:w-full"
                  onClick={handleComplete}
                  disabled={actionsBusy}
                  loading={completing}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />Complete
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="max-md:w-full"
                onClick={handleDeactivate}
                disabled={actionsBusy}
                loading={deactivating}
              >
                Deactivate
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="max-md:w-full"
                onClick={() => handleReschedule('week')}
                disabled={actionsBusy}
                loading={rescheduling === 'week'}
              >
                Due +1 Week
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="max-md:w-full"
                onClick={() => handleReschedule('month')}
                disabled={actionsBusy}
                loading={rescheduling === 'month'}
              >
                Due +1 Month
              </Button>
            </div>
          )}
          {/* No Save on a locked task: every field is disabled, so the button
              could only ever re-post what is already stored. A Close takes its
              place rather than leaving the footer an empty strip — the header X
              is the only other way out. */}
          {taskInactive ? (
            <Button variant="secondary" className="max-md:w-full" onClick={onClose}>
              Close
            </Button>
          ) : (
            <Button className="max-md:w-full" onClick={handleSubmit} disabled={actionsBusy} loading={saving}>
              {isEdit ? 'Save Changes' : 'Add Task'}
            </Button>
          )}
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
        {loadingItem && <p className="text-xs text-slate-500">Loading task details…</p>}
        {taskInactive && (
          <p className="text-sm text-slate-700 bg-slate-100 border border-slate-300 rounded px-3 py-2">
            This task is {taskStatusLabel === 'Completed' ? 'complete' : 'inactive'} and can no
            longer be changed. Status: <strong>{taskStatusLabel || 'Inactive'}</strong>
          </p>
        )}

        {/* ── Section: Details (legacy lines 668-748) ──────────────────── */}
        <section className="rounded-md border border-slate-200">
          <header className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-700">Details</header>
          <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {categoryFilterOptions.length > 1 && (
              <FormField label="Category" helpText="Narrows the Type list below">
                <Select
                  options={categoryFilterOptions}
                  value={typeCategoryFilter}
                  onChange={v => setTypeCategoryFilter(v ?? '')}
                  clearable
                  disabled={taskInactive}
                />
              </FormField>
            )}
            <FormField label="Type">
              <Select
                options={cannedTaskOptions}
                value={form.taskID || ''}
                onChange={v => set('taskID', v ?? '')}
                searchable
                disabled={taskInactive}
              />
            </FormField>
            {showCustomNameInput && (
              <FormField label="Custom Task Name" required>
                <Input
                  value={form.customTaskName}
                  onChange={e => set('customTaskName', e.target.value)}
                  maxLength={35}
                  required
                  disabled={taskInactive}
                />
              </FormField>
            )}
            <FormField label="Priority">
              <Select
                options={PRIORITY_OPTIONS}
                value={form.priority}
                onChange={v => set('priority', v ?? '2')}
                disabled={taskInactive}
              />
            </FormField>
            {isEdit && (
              <FormField label="Complete Before Due">
                <Select
                  options={ALLOW_COMPLETION_OPTIONS}
                  value={form.required}
                  onChange={v => set('required', v ?? '0')}
                  disabled
                />
              </FormField>
            )}
          </div>
        </section>

        {/* ── Section: Dates (legacy lines 752-941) ────────────────────── */}
        <section className="rounded-md border border-slate-200">
          <header className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-700">Dates</header>
          <div className="p-3 space-y-3">
            <FormField label="Date to Perform">
              {/* One column on a phone. At 375px this card is ~317px wide inside its
                  padding, so two columns gave each control ~154px and truncated the
                  longer DATE_TYPE_OPTIONS labels ("next appointment"). It also left
                  Custom Date below at twice the width of everything above it, which is
                  what SCRUM-1353 reported. */}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                <Select
                  options={TIME_AMOUNT_OPTIONS}
                  value={form.timeAmount}
                  onChange={v => set('timeAmount', v ?? '0')}
                  searchable
                  disabled={taskInactive}
                />
                <Select
                  options={TIME_UNIT_OPTIONS}
                  value={form.timeType}
                  onChange={v => set('timeType', v ?? '1')}
                  disabled={taskInactive}
                />
                <Select
                  options={BEFORE_AFTER_OPTIONS}
                  value={form.beforeOrAfter}
                  onChange={v => set('beforeOrAfter', v ?? '')}
                  disabled={taskInactive}
                />
                <Select
                  options={DATE_TYPE_OPTIONS}
                  value={form.dateType}
                  onChange={v => set('dateType', v ?? '2')}
                  disabled={taskInactive}
                />
              </div>
            </FormField>

            {showCustomDate && (
              /*
                SCRUM-1404. `Input type="date"` does carry `form-input-base`, so the
                width clamp from index.css already applied — but it renders no calendar
                icon, which is half of what the ticket asks for, and the field read as
                inconsistent with the Portal. DatePicker is the same native control with
                the icon and the placeholder overlay, and it is what the appointment
                modal's Date field already uses.
              */
              <DatePicker
                ref={customDateRef}
                label="Custom Date"
                required
                value={form.customDate}
                onChange={(value) => set('customDate', value)}
                error={customDateMissing && !form.customDate ? 'Choose a date.' : undefined}
                disabled={taskInactive}
              />
            )}


            <FormField label="Occurrence">
              {/* This row is a sentence, not a field list, so it wraps rather than
                  stacking. `Select`'s own root is `w-full` — without displacing that,
                  every control claimed a whole line and left "times," and "apart."
                  orphaned on lines of their own. `cn` is tailwind-merge, so `w-auto`
                  in wrapperClassName wins over the component's `w-full`. */}
              <div className="flex flex-wrap gap-3 items-end">
                <Select
                  options={OCCURRENCE_OPTIONS}
                  value={form.recurring}
                  onChange={v => set('recurring', v ?? '0')}
                  disabled={taskInactive}
                  wrapperClassName="w-auto flex-1 min-w-[7rem] md:flex-none"
                />
                {isRecurring && (
                  <>
                    <Select
                      options={RECURRING_NUMBER_OPTIONS}
                      value={form.recurringNumber}
                      onChange={v => set('recurringNumber', v ?? '2')}
                      searchable
                      disabled={taskInactive}
                      wrapperClassName="w-auto flex-1 min-w-[7rem] md:flex-none"
                    />
                    <span className="text-xs text-slate-500">times,</span>
                    <Select
                      options={RECURRING_AMOUNT_OPTIONS}
                      value={form.recurringAmount}
                      onChange={v => set('recurringAmount', v ?? '1')}
                      searchable
                      disabled={taskInactive}
                      wrapperClassName="w-auto flex-1 min-w-[7rem] md:flex-none"
                    />
                    <Select
                      options={TIME_UNIT_OPTIONS}
                      value={form.recurringType}
                      onChange={v => set('recurringType', v ?? '1')}
                      disabled={taskInactive}
                      wrapperClassName="w-auto flex-1 min-w-[7rem] md:flex-none"
                    />
                    <span className="text-xs text-slate-500">apart.</span>
                  </>
                )}
              </div>
            </FormField>
          </div>
        </section>

        {/* ── Section: Notes (legacy lines 943-958) ────────────────────── */}
        <section className="rounded-md border border-slate-200">
          <header className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-700">Notes</header>
          <div className="p-3">
            <Textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={3}
              disabled={taskInactive}
            />
          </div>
        </section>

        {/* ── Section: Patient & Plan ──────────────────────────────────
            New tasks pick them — unless the caller already fixed the plan
            (`initialPlan`), in which case the pair shows read-only like edit
            mode does; existing tasks show them read-only. Legacy never
            allowed re-parenting a task, and assembleSavePayload deliberately
            omits taskPatientID/planID on edit, so edit mode is display-only —
            but it MUST display, or a task opened from a dashboard card gives no
            clue who it is for. Values come from `item` (the list row), not the
            getTask refetch, whose response carries no patientName. */}
        <section className="rounded-md border border-slate-200">
          <header className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-700">Patient & Plan</header>
          <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {isEdit || lockedPlan ? (
              <>
                <FormField label="Patient">
                  {patientId > 0 && patientLabel ? (
                    <PatientLink
                      patientId={patientId}
                      patientName={patientLabel}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:underline"
                    >
                      {patientLabel}
                    </PatientLink>
                  ) : (
                    // taskPatientID=0 with a name is real: the list SQL falls the
                    // name back to the plan's patient while emitting the raw FK,
                    // so the name is displayable but not linkable.
                    <span className="text-sm text-slate-700">{patientLabel || 'No patient'}</span>
                  )}
                </FormField>
                <FormField label="Plan">
                  <span className="text-sm text-slate-700">{planLabel}</span>
                </FormField>
              </>
            ) : (
              <>
                <FormField label="Patient">
                  <PatientSearchInput
                    selectedPatient={selectedPatient}
                    onSelect={(patient) => {
                      setSelectedPatient(patient);
                      set('patientID', patient ? String(patient.patientId) : '');
                      set('planID', '');
                    }}
                    placeholder="Search patients…"
                  />
                </FormField>
                <FormField label="Plan">
                  <Select
                    options={planOptions}
                    value={form.planID || null}
                    onChange={v => set('planID', v ?? '')}
                    searchable
                    clearable
                    placeholder={selectedPatient ? 'Select plan…' : 'Select a patient first'}
                    disabled={!selectedPatient}
                  />
                </FormField>
              </>
            )}
          </div>
        </section>

        {/* ── Section: Assignments (legacy lines 960-1505) ─────────────── */}
        <section className="rounded-md border border-slate-200">
          <header className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-700">Assignments</header>
          <div className="p-3 space-y-3">
            {isEdit && (
              <FormField label="Type">
                <Select
                  options={AUTO_TASK_TYPE_OPTIONS}
                  value={form.autoTaskID}
                  onChange={v => set('autoTaskID', v ?? '0')}
                  disabled
                />
              </FormField>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Assigned To">
                {isEdit ? (
                <Select
                  options={assignedToOptions}
                  value={form.assignedToID}
                  onChange={v => set('assignedToID', v ?? 'coord')}
                  searchable
                  disabled={taskInactive}
                />
                ) : (
                <Select
                  options={simpleAssignedToOptions}
                  value={assignedCategory}
                  onChange={v => {
                    if (v === 'myself') set('assignedToID', 'myself');
                    else if (v === 'user') set('assignedToID', 'user');
                    else if (v === 'ftp') set('assignedToID', 'ftp');
                    else if (v === 'group') {
                      const first = groupPickerOptions[0]?.value ?? '';
                      set('assignedToID', first);
                    }
                  }}
                />
                )}
              </FormField>
              {form.assignedToID === 'user' && (
                canPickMultipleUsers ? (
                <FormField label="Specific Users">
                  <SearchableMultiSelect
                    options={userOptions}
                    selectedValues={form.assignedUserIDs}
                    onSelectionChange={v => set('assignedUserIDs', v)}
                    disabled={taskInactive}
                    placeholder="Select users…"
                    searchPlaceholder="Search users…"
                    emptyMessage="No users found."
                  />
                </FormField>
                ) : (
                <FormField label="Specific User">
                  <Select
                    options={userOptions}
                    value={form.assignedUserID || null}
                    onChange={v => set('assignedUserID', v ?? '')}
                    searchable
                    clearable
                    disabled={taskInactive}
                    placeholder="Select user…"
                  />
                </FormField>
                )
              )}
              {!isEdit && assignedCategory === 'group' && (
                <FormField label="Group">
                  <Select
                    options={groupPickerOptions}
                    value={form.assignedToID}
                    onChange={v => set('assignedToID', v ?? '')}
                    searchable
                  />
                </FormField>
              )}
              {form.assignedToID === 'ftp' && (
                <div className="flex items-end">
                  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    <Bot className="h-4 w-4 text-slate-400" />
                    Auto-managed by Prospera
                  </div>
                </div>
              )}
            </div>

            {/* Conditional autoTaskID-driven content fields */}
            {showTagField && (
              <FormField label="Tag">
                <Select
                  options={tagOptions}
                  value={form.tagCustomID}
                  onChange={v => set('tagCustomID', v ?? '0')}
                  searchable
                  disabled={taskInactive}
                />
              </FormField>
            )}

            {showEmailFields && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField label="Send To">
                  <Select
                    options={emailSendToOptions}
                    value={form.sendEmailToID}
                    onChange={v => set('sendEmailToID', v ?? '0')}
                    disabled={taskInactive}
                  />
                </FormField>
                <FormField label="Header / Footer">
                  <Select
                    options={emailHeaderOptions}
                    value={form.emailHeaderFooterID}
                    onChange={v => set('emailHeaderFooterID', v ?? '0')}
                    searchable
                    disabled={taskInactive}
                  />
                </FormField>
                <FormField label="Content">
                  <Select
                    options={emailContentOptions}
                    value={form.emailContentID}
                    onChange={v => set('emailContentID', v ?? '0')}
                    searchable
                    disabled={taskInactive}
                  />
                </FormField>
              </div>
            )}

            {showSMSFields && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField label="Send To">
                  <Select
                    options={SMS_SEND_TO_OPTIONS}
                    value={form.sendSMSToID}
                    onChange={v => set('sendSMSToID', v ?? '0')}
                    disabled={taskInactive}
                  />
                </FormField>
                <FormField label="Content">
                  <Select
                    options={smsContentOptions}
                    value={form.smsContentID}
                    onChange={v => set('smsContentID', v ?? '0')}
                    searchable
                    disabled={taskInactive}
                  />
                </FormField>
              </div>
            )}

            {showEmailSurveyFields && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField label="Survey">
                  <Select
                    options={surveyOptions}
                    value={form.surveyID}
                    onChange={v => set('surveyID', v ?? '0')}
                    searchable
                    disabled={taskInactive}
                  />
                </FormField>
                <FormField label="Content">
                  <Select
                    options={emailContentOptions}
                    value={form.surveyEmailContentID}
                    onChange={v => set('surveyEmailContentID', v ?? '0')}
                    searchable
                    disabled={taskInactive}
                  />
                </FormField>
              </div>
            )}

            {showSMSSurveyFields && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField label="Survey">
                  <Select
                    options={surveyOptions}
                    value={form.surveyID}
                    onChange={v => set('surveyID', v ?? '0')}
                    searchable
                    disabled={taskInactive}
                  />
                </FormField>
                <FormField label="Content">
                  <Select
                    options={smsContentOptions}
                    value={form.surveySmsContentID}
                    onChange={v => set('surveySmsContentID', v ?? '0')}
                    searchable
                    disabled={taskInactive}
                  />
                </FormField>
              </div>
            )}

            {/* Triggers — read-only display per Phase 0 decisions #1 and #2 */}
            {isEdit && triggers && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1">Assign at Stage</div>
                  {triggers.addAtStage.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">None</p>
                  ) : (
                    <ul className="text-sm text-slate-700 space-y-0.5">
                      {triggers.addAtStage.map((id) => (
                        <li key={`as-${id}`}>{stageTitleMap[id] ?? `Stage #${id}`}</li>
                      ))}
                    </ul>
                  )}
                  <div className="text-xs font-medium text-slate-700 mt-3 mb-1">Assign at Appt Status</div>
                  {triggers.addAtApptStage.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">None</p>
                  ) : (
                    <ul className="text-sm text-slate-700 space-y-0.5">
                      {triggers.addAtApptStage.map((id) => (
                        <li key={`aa-${id}`}>{APPT_STATUS_LABELS[id] ?? `Status #${id}`}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1">Remove at Stage</div>
                  {triggers.removeAtStage.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">None</p>
                  ) : (
                    <ul className="text-sm text-slate-700 space-y-0.5">
                      {triggers.removeAtStage.map((id) => (
                        <li key={`rs-${id}`}>{stageTitleMap[id] ?? `Stage #${id}`}</li>
                      ))}
                    </ul>
                  )}
                  <div className="text-xs font-medium text-slate-700 mt-3 mb-1">Remove at Appt Status</div>
                  {triggers.removeAtApptStage.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">None</p>
                  ) : (
                    <ul className="text-sm text-slate-700 space-y-0.5">
                      {triggers.removeAtApptStage.map((id) => (
                        <li key={`ra-${id}`}>{APPT_STATUS_LABELS[id] ?? `Status #${id}`}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <p className="md:col-span-2 text-xs italic text-slate-500">
                  Stage and appointment-status triggers are managed at the task group level and cannot be edited from the task itself.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ── Section: Documents (only when autoTaskID=1, per Phase 0 decision #7) ── */}
        {showDocumentsSection && (
          <section className="rounded-md border border-slate-200">
            <header className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-700">Attach Documents</header>
            <div className="p-3 max-h-56 overflow-y-auto space-y-3">
              {docPackets.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1">Document Packets</div>
                  {docPackets.map((p) => {
                    const value = `P_${p.documentPacketId}`;
                    return (
                      <label key={value} className="flex items-center gap-2 text-sm py-0.5">
                        <input
                          type="checkbox"
                          checked={attachmentSet.has(value)}
                          onChange={() => toggleAttachment(value)}
                          disabled={taskInactive}
                        />
                        <span><strong>{p.name}</strong> {p.description ? <span className="text-slate-500">— {p.description}</span> : null}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {documentsGrouped.map((group) => (
                <div key={group.categoryName}>
                  <div className="text-xs font-semibold text-slate-700 mb-1">{group.categoryName}</div>
                  {group.documents.map((doc) => {
                    const value = String(doc.documentID);
                    return (
                      <label key={value} className="flex items-center gap-2 text-sm py-0.5">
                        <input
                          type="checkbox"
                          checked={attachmentSet.has(value)}
                          onChange={() => toggleAttachment(value)}
                          disabled={taskInactive}
                        />
                        <span><strong>{doc.title}</strong> <span className="text-slate-500">({doc.fileName})</span></span>
                      </label>
                    );
                  })}
                </div>
              ))}
              {docPackets.length === 0 && documentsGrouped.length === 0 && (
                <p className="text-sm text-slate-500 italic">No documents available.</p>
              )}
            </div>
          </section>
        )}

        {/* ── Section: History (edit only) — diffed plan_tasks_history feed ── */}
        {isEdit && (
          <section className="rounded-md border border-slate-200">
            <header className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-700">History</header>
            <div className="p-3">
              {historyLoading ? (
                <p className="text-sm text-slate-400 text-center py-4">Loading history…</p>
              ) : historyError ? (
                <p className="text-sm text-red-500 text-center py-4">Failed to load history.</p>
              ) : history.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No history entries.</p>
              ) : (
                <div className="space-y-2">
                  {[...history].reverse().map((entry, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                      <div className="flex-shrink-0 mt-0.5 p-1.5 rounded-lg text-violet-600 bg-violet-50">
                        <History className="h-4 w-4 shrink-0" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-2 mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{entry.verb ?? 'Changed'}</span>
                          <span className="text-sm font-bold text-slate-800">{formatHistoryDateTime(entry.date)}</span>
                          {entry.userName && <span className="text-[11px] text-slate-400">by {entry.userName}</span>}
                          {entry.triggerType && entry.triggerType !== 'manual' && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{entry.triggerType}</span>
                          )}
                        </div>
                        <table className="text-sm">
                          <tbody>
                            {entry.changes.map((c, j) => (
                              <tr key={j}>
                                <td className="pr-4 py-0.5 font-medium text-slate-600 whitespace-nowrap align-top w-px">{c.field}</td>
                                <td className="pr-4 py-0.5 text-slate-400 whitespace-nowrap align-top w-px">{c.verb}</td>
                                <td className="py-0.5 text-slate-900 align-top">{c.newValue || '(empty)'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* hidden form submit (Enter inside inputs commits) */}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

// ─── Complete Modal ──────────────────────────────────────────────────────────

export function CompleteModal({ task, onClose, onSaved }: { task: Task; onClose: () => void; onSaved: () => void }) {
  const [notes, setNotes] = useState(task.notes ?? '');
  const [saving, setSaving] = useState(false);
  const { reschedule, rescheduling } = useTaskReschedule();

  async function handleComplete() {
    setSaving(true);
    try {
      await tasksSvc.completeTask(task.planTaskID, { notes: notes || undefined });
      onSaved(); onClose();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  }

  return (
    <Modal isOpen onClose={onClose} title="Complete Task" size="md"
      footer={
        /* The shared footer row is `justify-end` with no wrap, so a row wider
           than the panel overflows off the LEFT edge, where nothing can scroll
           it back — which is why "Due +1 Week" vanished at 375px. Own the
           layout here instead. Every mobile rule is `max-md:`-prefixed so the
           desktop row is untouched: the unprefixed classes below are today's
           computed layout verbatim. */
        <div className="flex w-full items-center justify-end gap-3 max-md:flex-col max-md:gap-2">
          <div className="mr-auto flex items-center gap-2 max-md:mr-0 max-md:grid max-md:w-full max-md:grid-cols-2">
            <Button
              variant="secondary"
              className="max-md:w-full"
              onClick={() => reschedule(task.planTaskID, 'week', () => { onSaved(); onClose(); })}
              loading={rescheduling === 'week'}
              disabled={saving || rescheduling !== null}
            >
              Due +1 Week
            </Button>
            <Button
              variant="secondary"
              className="max-md:w-full"
              onClick={() => reschedule(task.planTaskID, 'month', () => { onSaved(); onClose(); })}
              loading={rescheduling === 'month'}
              disabled={saving || rescheduling !== null}
            >
              Due +1 Month
            </Button>
          </div>
          <div className="flex items-center gap-3 max-md:grid max-md:w-full max-md:grid-cols-2">
            <Button variant="ghost" className="max-md:w-full" onClick={onClose} disabled={saving || rescheduling !== null}>Cancel</Button>
            <Button className="max-md:w-full" onClick={handleComplete} loading={saving} disabled={rescheduling !== null}>
              <CheckCircle2 className="h-4 w-4 mr-1" />Complete
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Mark <strong>{task.name}</strong> as complete?</p>
        <FormField label="Completion Notes (optional)">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Add any notes about completion…" />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function TasksPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navToPatient = usePatientNavigation();
  const setAIContext = useAISidebarStore((s) => s.setContext);
  const routeFlTasks = parseTaskFilterTab(searchParams.get('flTasks'));
  const [items, setItems] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [sortKey, setSortKey] = useState<string>('lastDue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Patient quick-view side panel — clicking a patient name on a row opens the
  // right-side detail bar (same component used by /patients) without leaving
  // the task list. Mirrors the pattern from PatientsPage / CRMPage.
  const [selectedPatient, setSelectedPatient] = useState<{ patientId: number; patientName: string } | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const { detail: patientDetail, loading: patientDetailLoading } = usePatientDetail(
    selectedPatient ? String(selectedPatient.patientId) : null,
  );

  const selectPatient = useCallback((patientId: number, patientName: string) => {
    if (!patientId) return;
    setSelectedPatient({ patientId, patientName });
    setPanelExpandTrigger((trigger) => trigger + 1);
    setAIContext('patient', patientId);
  }, [setAIContext]);

  const toast = useToast();
  const { hasMinUserLevel, user: authUser } = useAuth();
  const isAdmin = hasMinUserLevel(3);
  const isFilterAdmin = hasMinUserLevel(3);
  const visibleFilterConfigs = TASK_FILTER_CONFIGS;
  // Shared filters with /tasks dashboard via storage key 'tasks'
  const filterState = useLocalDashboardFilters(visibleFilterConfigs, 'tasks');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const filterParams = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  const [flTasks, setFlTasksRaw] = useState(routeFlTasks);

  // Reference data
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [taskGroups, setTaskGroups] = useState<TaskGroup[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [assignedToRoles, setAssignedToRoles] = useState<AccountRoleEntry[]>([]);
  const [allAccountRoles, setAllAccountRoles] = useState<AccountRoleEntry[] | null>(null);

  // AI page context
  const updateAIContext = usePageContext('All Tasks');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<Task | null>(null);
  const [completeItem, setCompleteItem] = useState<Task | null>(null);
  const [deleteItem, setDeleteItem] = useState<Task | null>(null);

  // Reset to page 1 and clear selection when filters change
  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [filterParams]);

  // Load reference data
  useEffect(() => {
    void listUsers().then(setUsers).catch(() => {/* ignore */});
    void tasksSvc.listGroups({ page: 1, perPage: 200 })
      .then(res => setTaskGroups(res.data ?? []))
      .catch(() => {/* ignore */});
    void listFacilities().then(r => setFacilities(r ?? [])).catch(() => {/* ignore */});
    void listMyRoles().then(r => setAssignedToRoles(r ?? [])).catch(() => {/* ignore */});
    if (isFilterAdmin) {
      void listAccountRoles().then(r => setAllAccountRoles(r ?? [])).catch(() => {/* ignore */});
    }
  }, [isFilterAdmin]);

  useEffect(() => {
    setFlTasksRaw(routeFlTasks);
    setPage(1);
    setSelectedIds(new Set());
  }, [routeFlTasks]);

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDir(direction);
    setPage(1);
  }, []);

  // Trigger a manual refresh after mutations (delete, complete, etc.) without
  // re-deriving the deps array of the list effect.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const queryClient = useQueryClient();
  // Every task write on this page ends here. The list itself is not a query, so
  // the shared task counts (the sidebar's Tasks count, the Dashboard's Pending
  // Tasks badge) are refreshed explicitly.
  const refreshList = useCallback(() => {
    setRefreshNonce((n) => n + 1);
    void queryClient.invalidateQueries({ queryKey: MY_TASK_COUNTS_KEY });
  }, [queryClient]);

  // Bulk actions (admin level 3+)
  const [bulkCompleting, setBulkCompleting] = useState(false);
  const [bulkDeactivating, setBulkDeactivating] = useState(false);

  /**
   * The ids a bulk action may actually touch. The checkboxes already refuse a
   * closed task (`isRowSelectable` below), but a selection outlives the rows it
   * was made from: complete a task in another tab, or let the list refresh under
   * a standing selection, and an id for a now-closed task is still in the set.
   * Re-check against the rows on screen every time rather than trusting it.
   */
  const actionableSelectedIds = useCallback(() => {
    const open = new Set(items.filter(isTaskOpen).map((t) => t.planTaskID));
    return Array.from(selectedIds)
      .map(Number)
      .filter((id) => open.has(id));
  }, [items, selectedIds]);

  const handleBulkComplete = useCallback(async () => {
    const ids = actionableSelectedIds();
    if (ids.length === 0) return;
    setBulkCompleting(true);
    await Promise.allSettled(ids.map((id) => tasksSvc.completeTask(id)));
    setSelectedIds(new Set());
    refreshList();
    setBulkCompleting(false);
  }, [actionableSelectedIds, refreshList]);

  // De-activate is NOT bulk-complete with a note (that is what it used to be,
  // and it made every de-activated task count as completed) — it terminates
  // each task as 'Inactive'. See tasksSvc.deactivateTask.
  const handleBulkDeactivate = useCallback(async () => {
    const ids = actionableSelectedIds();
    if (ids.length === 0) return;
    setBulkDeactivating(true);
    const userFullName = authUser?.displayName?.trim() || 'unknown user';
    const notes = `Manually de-activated by ${userFullName}`;
    await Promise.allSettled(ids.map((id) => tasksSvc.deactivateTask(id, { notes })));
    setSelectedIds(new Set());
    refreshList();
    setBulkDeactivating(false);
  }, [actionableSelectedIds, refreshList, authUser]);

  // List fetch. Uses AbortController so rapid tab clicks / filter changes
  // cancel the in-flight request and never let a late response replace what
  // the user is currently looking at.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const params: tasksSvc.TaskFiltersParams = {
      ...filterParams,
      flTasks,
      page,
      pageSize: perPage,
      sortBy: sortKey,
      sortDir,
    };

    tasksSvc.listTasks(params, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        setItems(res.data ?? []);
        setTotal(res.meta?.total ?? 0);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        // Deliberately does not surface the server's message. That string is
        // written for whoever reads the logs ("Failed to fetch plan tasks"),
        // not for the person at the desk. The diagnostic detail is reported
        // to Sentry by PlanTasksController::index instead.
        toast.error('Could not load tasks', 'Please refresh the page. If it keeps happening, contact support.');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [page, flTasks, filterParams, sortKey, sortDir, refreshNonce]);

  // Tab badge counts. Mirrors TasksDashboardPage so badges respect the
  // sidebar filters (facility, assignedTo, etc.) without including flTasks
  // itself — the buckets are computed across all statuses.
  const [tabCounts, setTabCounts] = useState<tasksSvc.TaskCounts | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    tasksSvc.getTaskCounts(filterParams, { signal: controller.signal })
      .then((c) => {
        if (controller.signal.aborted) return;
        setTabCounts(c);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        // Background badge counts — log but don't duplicate the list-fetch toast.
        console.error('Failed to load task tab counts', err);
      });
    return () => controller.abort();
  }, [filterParams, refreshNonce]);

  // Keep AI assistant aware of current page state
  useEffect(() => {
    const activeTabLabel = TASK_FILTER_TABS.find(t => t.value === flTasks)?.label ?? 'All Entries';
    const parts: string[] = [`Tab: ${activeTabLabel}`];
    const assignedTo = Array.isArray(effectiveValues['assignedTo']) ? effectiveValues['assignedTo'] : [];
    if (assignedTo.length) parts.push(`Assigned to: ${assignedTo.join(', ')}`);
    const priorities = Array.isArray(effectiveValues['priority']) ? effectiveValues['priority'] : [];
    if (priorities.length === 1) parts.push(`Priority: ${priorities[0]}`);
    else if (priorities.length > 1) parts.push(`Priority: ${priorities.join(', ')}`);
    const groups = Array.isArray(effectiveValues['taskGroup']) ? effectiveValues['taskGroup'] : [];
    if (groups.length === 1) {
      const name = taskGroups.find(g => g.taskGroupID === Number(groups[0]))?.name ?? groups[0];
      parts.push(`Group: ${name}`);
    }
    const search = extractSearch(effectiveValues);
    if (search) parts.push(`Search: "${search}"`);

    const totalPages = Math.ceil(total / perPage) || 1;
    updateAIContext({
      filters: parts.join(', '),
      dataSummary: `${total} task${total !== 1 ? 's' : ''} total, page ${page} of ${totalPages}`,
    });
  }, [flTasks, effectiveValues, total, page, taskGroups, updateAIContext]);

  async function handleDelete() {
    if (!deleteItem) return;
    await tasksSvc.deleteTask(deleteItem.planTaskID);
    setDeleteItem(null);
    refreshList();
  }

  // ── Sidebar filter options ───────────────────────────────────────────────

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      facility: (facilities ?? []).filter(f => f.active).map(f => ({ value: String(f.facilityId), label: f.facilityName })),
      memberStatus: TASK_MEMBER_STATUS_OPTIONS,
      assignedTo: isFilterAdmin
        ? buildAssignedToOptions(assignedToRoles, allAccountRoles, users)
        : buildAssignedToOptions(assignedToRoles),
      priority: [
        { value: 'low', label: 'Low' },
        { value: 'normal', label: 'Medium' },
        { value: 'high', label: 'High' },
      ],
      taskGroup: taskGroups.map(g => ({ value: String(g.taskGroupID), label: g.name })),
    }),
    [facilities, assignedToRoles, allAccountRoles, users, taskGroups, isFilterAdmin],
  );

  // ── Columns: Photo | Task Name | Patient | Plan Type | Facility | Status | Priority | Occurs | Added | Due | Done
  const columns: DataTableColumn<Task>[] = [
    {
      key: 'assignedToImage',
      header: '',
      render: (r) => {
        if (r.assignedToName?.trim()) {
          return <Avatar src={r.assignedToImage ?? undefined} name={r.assignedToName} size="sm" />;
        }
        if (r.assignedToID) {
          return (
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-200">
              <Bot className="h-4 w-4 text-slate-500" />
            </span>
          );
        }
        return null;
      },
    },
    {
      key: 'name',
      header: <ColHeader label="Task Name" tip="The name or title of the task" />,
      sortable: true,
      render: (r) => (
        <span className="font-medium text-slate-900">{r.name}</span>
      ),
    },
    {
      key: 'patientName',
      header: <ColHeader label="Patient" tip="The patient this task is assigned to" />,
      sortable: true,
      render: (r) => {
        if (!r.patientName || !r.taskPatientID) {
          return <span className="text-sm text-slate-400">—</span>;
        }
        return (
          <button
            type="button"
            className="text-left text-sm text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              selectPatient(r.taskPatientID!, r.patientName!);
            }}
          >
            {r.patientName}
          </button>
        );
      },
    },
    {
      key: 'planTypeName',
      header: <ColHeader label="Plan Type" tip="The type of treatment plan linked to this task" />,
      sortable: true,
      render: (r) => {
        // Blank when the task has no plan, or the plan has no plan type.
        if (!r.planID || !r.planTypeName) {
          return null;
        }
        return (
          <Link
            to={`/plans/${r.planID}`}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            onClick={e => e.stopPropagation()}
          >
            {r.planTypeName}
          </Link>
        );
      },
    },
    {
      key: 'memberStatus',
      header: <ColHeader label="Member" tip="Whether the patient has an active membership" />,
      sortable: true,
      render: (r) => {
        if (!r.taskPatientID) return <span className="text-sm text-slate-400">—</span>;
        return (
          <Badge variant={r.memberStatus === 1 ? 'success' : 'neutral'} size="sm">
            {r.memberStatus === 1 ? 'Member' : 'Non-Member'}
          </Badge>
        );
      },
    },
    {
      key: 'facilityName',
      header: <ColHeader label="Facility" tip="The clinic or office location for this task" />,
      sortable: true,
      render: (r) => (
        <span className="text-sm text-slate-600">{r.facilityName ?? '—'}</span>
      ),
    },
    {
      key: 'derivedStatus',
      header: <ColHeader label="Status" tip="Current status: Past Due, Due, Upcoming, Completed, or Inactive — sorts most urgent first" />,
      sortable: true,
      render: (r) => (
        <Badge variant={statusVariant(r.derivedStatus)} size="sm">
          {r.derivedStatus ?? 'N/A'}
        </Badge>
      ),
    },
    {
      key: 'priority',
      header: <ColHeader label="Priority" tip="Task urgency level: Low, Medium, or High" />,
      sortable: true,
      render: (r) => (
        <Badge variant={priorityVariant(r.priority)} size="sm">
          {priorityLabel(r.priority)}
        </Badge>
      ),
    },
    {
      key: 'recurring',
      header: <ColHeader label="Occurs" tip="Whether this task happens once or recurs on a schedule" />,
      sortable: true,
      render: (r) => <span className="text-sm text-slate-600">{r.recurring === 1 ? 'Recurring' : 'Once'}</span>,
    },
    {
      key: 'dateStarted',
      header: <ColHeader label="Added" tip="The date this task was created" />,
      sortable: true,
      render: (r) => <span className="text-sm text-slate-500 whitespace-nowrap">{formatDate(r.dateStarted)}</span>,
    },
    {
      key: 'lastDue',
      header: <ColHeader label="Due" tip="The date this task is due to be completed" />,
      sortable: true,
      render: (r) => <span className="text-sm text-slate-500 whitespace-nowrap">{formatDate(r.lastDue)}</span>,
    },
    {
      key: 'dateCompleted',
      header: <ColHeader label="Done" tip="Completion date, or click Done to mark the task complete" />,
      sortable: true,
      render: (r) => {
        if (r.dateCompleted && r.dateCompleted !== '0000-00-00') {
          return <span className="text-sm text-green-600 whitespace-nowrap">{formatDate(r.dateCompleted)}</span>;
        }
        // Resolve the label rather than string-matching `derivedStatus`, which
        // is absent on rows that did not come from a list endpoint.
        if (resolveTaskStatusLabel(r) === 'Inactive' || r.required === 1) {
          return <span className="text-sm text-slate-400">—</span>;
        }
        return (
          <TaskDoneButton
            taskId={r.planTaskID}
            onComplete={() => setCompleteItem(r)}
            onRescheduled={() => refreshList()}
          />
        );
      },
    },
  ];

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={visibleFilterConfigs}
            values={effectiveValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={filterState.setFilter}
            onNegateChange={filterState.setNegation}
            onClear={filterState.clearFilter}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="tasks"
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
        rightPanel={selectedPatient ? (
          <DetailRightPanel expandTrigger={panelExpandTrigger}>
            <div className="flex h-full min-h-0 w-full min-w-0 flex-col bg-white">
              <div className="flex items-center justify-end gap-1 border-b border-slate-200 px-3 py-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={() => navToPatient({
                    patientId: selectedPatient.patientId,
                    name: selectedPatient.patientName,
                  })}
                >
                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                  Open
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <DetailProfile
                  detail={patientDetail}
                  loading={patientDetailLoading}
                  onSectionNavigate={(section) =>
                    navigate(`/patients/${selectedPatient.patientId}?section=${section}`)
                  }
                />
              </div>
            </div>
          </DetailRightPanel>
        ) : undefined}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-4 p-6 pb-0">
            <PageHeader
              variant="compact"
              title={(TASK_FILTER_TABS.find((t) => t.value === flTasks) ?? TASK_FILTER_TABS[5]).headerTitle}
              actions={
                <div className="flex items-center gap-2">
                  {isAdmin && selectedIds.size > 0 && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleBulkComplete}
                        disabled={bulkCompleting || bulkDeactivating}
                        loading={bulkCompleting}
                      >
                        Mark Done ({selectedIds.size})
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleBulkDeactivate}
                        disabled={bulkCompleting || bulkDeactivating}
                        loading={bulkDeactivating}
                      >
                        De-Activate ({selectedIds.size})
                      </Button>
                    </>
                  )}
                  <Button onClick={() => setShowCreate(true)}>
                    <Plus className="h-4 w-4 mr-1" />Add Task
                  </Button>
                </div>
              }
            />
            <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
              {TASK_FILTER_TABS.map((tab) => {
                const isActive = flTasks === tab.value;
                const count = tabCounts?.[tab.countKey] ?? 0;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    title={tab.tooltip}
                    onClick={() => {
                      setFlTasksRaw(tab.value);
                      setPage(1);
                      navigate(buildAllTasksHref(tab.value), { replace: true });
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      isActive
                        ? 'border-b-2 border-primary text-primary bg-primary/10'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {tab.label}
                    {tab.showBadge && count > 0 ? (
                      <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none ${
                        isActive ? 'bg-primary text-white' : 'bg-red-500 text-white'
                      }`}>
                        {count}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<Task>
              data={items}
              columns={columns}
              keyField="planTaskID"
              loading={loading}
              manualSort
              sortKey={sortKey}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              page={page}
              pageSize={perPage}
              total={total}
              onPageChange={setPage}
              onRowClick={setEditItem}
              selectable={isAdmin}
              // A closed task cannot be completed or de-activated, so it cannot
              // be checked either — the bulk bar acts on whatever is ticked.
              isRowSelectable={isTaskOpen}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              emptyTitle="No tasks found"
              emptyDescription="No tasks match the current filter."
            />
          </div>
        </div>
      </ListPageLayout>

      {/* Modals */}
      {showCreate && (
        <TaskModal
          onClose={() => setShowCreate(false)}
          onSaved={refreshList}
          users={users}
        />
      )}
      {editItem && (
        <TaskModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={refreshList}
          users={users}
        />
      )}
      {completeItem && (
        <CompleteModal
          task={completeItem}
          onClose={() => setCompleteItem(null)}
          onSaved={refreshList}
        />
      )}
      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Task"
        message={deleteItem ? `Delete "${deleteItem.name}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </>
  );
}
